import React from 'react';
import { Link } from 'react-router-dom';

const ErrorCodes = () => {
  const errorCodes = [
    {
      code: 400,
      title: 'Bad Request',
      description: 'La requête est malformée ou contient des paramètres invalides',
      examples: [
        'Paramètres manquants ou invalides',
        'Format JSON incorrect',
        'Données non conformes au schéma attendu'
      ],
      solution: 'Vérifiez les paramètres de votre requête et le format des données envoyées'
    },
    {
      code: 401,
      title: 'Unauthorized',
      description: 'Authentification requise ou échouée',
      examples: [
        'API Key manquante ou invalide',
        'JWT token manquant, expiré ou invalide',
        'Credentials incorrects'
      ],
      solution: 'Vérifiez que vous avez inclus un header Authorization valide avec votre JWT ou X-API-Key'
    },
    {
      code: 403,
      title: 'Forbidden',
      description: 'Accès refusé à la ressource',
      examples: [
        'Permissions insuffisantes',
        'Workspace non accessible',
        'Action non autorisée pour votre rôle'
      ],
      solution: 'Vérifiez vos permissions et que vous avez accès à la ressource demandée'
    },
    {
      code: 404,
      title: 'Not Found',
      description: 'La ressource demandée n\'existe pas',
      examples: [
        'Endpoint inexistant',
        'ID de ressource invalide',
        'Route non trouvée'
      ],
      solution: 'Vérifiez l\'URL de l\'endpoint et que la ressource existe'
    },
    {
      code: 429,
      title: 'Too Many Requests',
      description: 'Limite de taux dépassée',
      examples: [
        'Trop de requêtes dans la période allouée',
        'Rate limit atteint pour ce type d\'endpoint'
      ],
      solution: 'Attendez le temps indiqué dans le header Retry-After ou réduisez la fréquence de vos requêtes'
    },
    {
      code: 500,
      title: 'Internal Server Error',
      description: 'Erreur interne du serveur',
      examples: [
        'Problème temporaire du serveur',
        'Base de données indisponible',
        'Erreur inattendue'
      ],
      solution: 'Réessayez plus tard. Si le problème persiste, contactez le support'
    },
    {
      code: 503,
      title: 'Service Unavailable',
      description: 'Service temporairement indisponible',
      examples: [
        'Maintenance en cours',
        'Service surchargé',
        'Indisponibilité temporaire'
      ],
      solution: 'Réessayez dans quelques minutes'
    }
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1>❌ Codes d'Erreur</h1>
        <p>
          Documentation complète des codes d'erreur HTTP retournés par l'API WhakUp.
          Apprenez à identifier et résoudre les erreurs courantes.
        </p>
      </div>

      <div className="section">
        <h2>📋 Format des Erreurs</h2>
        <p>
          Toutes les erreurs retournées par l'API suivent un format JSON standardisé :
        </p>
        <div className="code-block">
{`{
  "error": {
    "code": 401,
    "message": "JWT Token not found",
    "details": "Header Authorization manquant"
  }
}`}
        </div>
        <p>
          Certaines erreurs peuvent également inclure un champ <code>errors</code> avec des détails de validation :
        </p>
        <div className="code-block">
{`{
  "error": {
    "code": 400,
    "message": "Validation failed",
    "errors": {
      "phoneNumber": "Format invalide. Utilisez le format international (+33123456789)",
      "email": "Email invalide"
    }
  }
}`}
        </div>
      </div>

      <div className="section">
        <h2>🔢 Codes d'Erreur HTTP</h2>
        
        {errorCodes.map((error, index) => (
          <div key={index} className="endpoint-card" style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <span style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: error.code === 429 ? '#fca130' : error.code >= 500 ? '#f93e3e' : error.code >= 400 ? '#dc3545' : '#667eea'
              }}>
                {error.code}
              </span>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.5rem', color: '#333' }}>{error.title}</h3>
                <p style={{ margin: '0.25rem 0 0 0', color: '#666' }}>{error.description}</p>
              </div>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ color: '#667eea', marginBottom: '0.5rem' }}>Exemples de situations :</h4>
              <ul style={{ marginLeft: '1.5rem', color: '#666' }}>
                {error.examples.map((example, i) => (
                  <li key={i}>{example}</li>
                ))}
              </ul>
            </div>

            <div className="alert alert-info" style={{ marginTop: '1rem' }}>
              <strong>💡 Solution :</strong> {error.solution}
            </div>
          </div>
        ))}
      </div>

      <div className="section">
        <h2>🛠️ Gestion des Erreurs dans votre Code</h2>
        
        <h3>JavaScript / Node.js</h3>
        <div className="code-block">
{`try {
  const response = await axios.post('/api/endpoint', data);
} catch (error) {
  if (error.response) {
    const { code, message, details } = error.response.data.error;
    
    switch (code) {
      case 401:
        console.error('Authentification requise:', message);
        // Réauthentifier
        break;
      case 429:
        const retryAfter = error.response.headers['retry-after'];
        console.error(\`Rate limit dépassé. Retry dans \${retryAfter}s\`);
        // Implémenter retry
        break;
      case 400:
        console.error('Erreur de validation:', error.response.data.error.errors);
        // Corriger les données
        break;
      default:
        console.error('Erreur API:', message);
    }
  }
}`}
        </div>

        <h3>PHP</h3>
        <div className="code-block">
{`try {
    $response = curl_exec($ch);
    $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
    
    if ($httpCode >= 400) {
        $errorData = json_decode($response, true);
        $error = $errorData['error'];
        
        switch ($httpCode) {
            case 401:
                error_log('Authentification requise: ' . $error['message']);
                break;
            case 429:
                $retryAfter = curl_getinfo($ch, CURLINFO_RETRY_AFTER);
                error_log("Rate limit dépassé. Retry dans {$retryAfter}s");
                break;
            case 400:
                error_log('Erreur de validation: ' . print_r($error['errors'], true));
                break;
        }
    }
} catch (Exception $e) {
    error_log('Erreur: ' . $e->getMessage());
}`}
        </div>

        <h3>Python</h3>
        <div className="code-block">
{`try:
    response = requests.post('/api/endpoint', json=data)
    response.raise_for_status()
except requests.exceptions.HTTPError as e:
    error_data = e.response.json()
    error = error_data.get('error', {})
    
    status_code = e.response.status_code
    
    if status_code == 401:
        print(f'Authentification requise: {error.get("message")}')
    elif status_code == 429:
        retry_after = e.response.headers.get('Retry-After')
        print(f'Rate limit dépassé. Retry dans {retry_after}s')
    elif status_code == 400:
        print(f'Erreur de validation: {error.get("errors")}')
    else:
        print(f'Erreur API: {error.get("message")}')
except requests.exceptions.RequestException as e:
    print(f'Erreur de requête: {e}')`}
        </div>
      </div>

      <div className="section">
        <h2>✅ Bonnes Pratiques</h2>
        <ul>
          <li><strong>Toujours vérifier le code de statut HTTP</strong> avant de traiter la réponse</li>
          <li><strong>Parser le message d'erreur</strong> pour afficher des messages utilisateur clairs</li>
          <li><strong>Implémenter un retry intelligent</strong> pour les erreurs 429 et 5xx</li>
          <li><strong>Logger les erreurs</strong> avec les détails pour le debugging</li>
          <li><strong>Gérer les erreurs de validation (400)</strong> en affichant les champs problématiques</li>
          <li><strong>Implémenter une gestion d'erreur centralisée</strong> dans votre SDK ou client HTTP</li>
        </ul>
      </div>

      <div className="section">
        <h2>📞 Besoin d'Aide ?</h2>
        <p>
          Si vous rencontrez une erreur non documentée ou que vous ne parvenez pas à résoudre un problème,
          consultez la <Link to="/support" style={{ color: '#667eea', textDecoration: 'none' }}>page Support</Link> 
          pour obtenir de l'aide.
        </p>
      </div>
    </div>
  );
};

export default ErrorCodes;
